import React, { FC, ReactNode, ReactNodeArray, useState } from 'react';
import { useLocation } from 'react-router-dom';
import OutsideClickHandler from 'react-outside-click-handler';
import { Box } from '@ch/common/components/box';
import GlobalStyle from '@ch/common/const/global-style';
import Header from '@ch/common/components/header';
import Footer from '@ch/common/components/footer';
import { Dropdown, DropdownItem, DropdownLink, DropdownMenu, DropdownToggle } from '@ch/common/components/dropdown';
import { useLang } from './use-lang';
import { useData } from './use-data';
import { assetsPath, RoutePath } from './const';
import { HomeNavigation } from './home/home-navigation';
import { Button } from '@ch/common/components/button';
import { FooterMenuLink } from '@ch/common/types/data';
import { Helmet } from 'react-helmet';
import LayoutStyled from './layout.styled';
import { HeaderStyled } from '@ch/common/components/header/header.styled';

const Layout: FC<{ children?: ReactNode | ReactNodeArray }> = ({ children }) => {
  const { pathname } = useLocation();
  const {
    data: {
      title,
      favicon,
      description,
      showTranslation,
      header: {
        appName,
        companyName,
        showCompanyName,
        logo,
        languages: { dropdown },
      },
      theme: { headerColor, footerColor, font },
      footer: { copyright_text: copyright, logo: footerLogo, menu, footerText },
    },
  } = useData();
  const { lang } = useLang();
  const [langToggle, setLangToggle] = useState(false);
  const handleLangToggle = () => {
    setLangToggle(!langToggle);
  };
  const currentLang = dropdown.find(({ short_name: shortName }) => shortName === lang);
  return (
    <div>
      <Helmet htmlAttributes={{ lang: currentLang?.short_name }}>
        <title>{title}</title>
        <link rel="icon" href={`${assetsPath}/${favicon}`} type="image/x-icon" />
        <meta name="description" content={description} />
      </Helmet>
      <GlobalStyle fontFamily={font?.family} />
      <Header
        {...{
          appName,
          showCompanyName,
          companyName,
          logo: `${assetsPath}/${logo}`,
          $color: 'primary',
          $bgColor: headerColor,
        }}
      >
        {pathname === RoutePath.HOME && (
          <Box $m={[0, 25, 0, 0]} $display={{ xs: 'none', md: 'block' }}>
            <HomeNavigation $color="red" $size="sm" $m={[0, 15]} />
          </Box>
        )}
        {showTranslation &&
          pathname !== `/${RoutePath.LEGAL_NOTICE}` &&
          ![RoutePath?.LEGAL_NOTICE_KOREAN, RoutePath?.LEGAL_NOTICE_JAPAN as string].includes(pathname) && (
            <OutsideClickHandler
              onOutsideClick={() => {
                setLangToggle(false);
              }}
            >
              <Dropdown>
                <DropdownToggle $active={langToggle} onClick={handleLangToggle}>
                  {currentLang.name}
                </DropdownToggle>
                <DropdownMenu active={langToggle}>
                  {dropdown.map(({ name, short_name: shortName }) => (
                    <DropdownItem key={shortName}>
                      <DropdownLink href={`?lang=${shortName}`}>{name}</DropdownLink>
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </OutsideClickHandler>
          )}
      </Header>
      <Box $display={{ xs: 'block', md: 'none' }}>
        <HeaderStyled {...{ $color: 'primary', $bgColor: headerColor }}>
          {pathname === RoutePath.HOME && (
            <Box $p={[5, 25, 25]}>
              <HomeNavigation $color="red" $size="sm" $m={[0, 15]} />
            </Box>
          )}
        </HeaderStyled>
      </Box>
      <LayoutStyled>{children}</LayoutStyled>
      <Footer {...{ copyright, logo: `${assetsPath}/${footerLogo}`, $bgColor: footerColor, footerText }}>
        {menu?.map(({ link, label, newTab }: FooterMenuLink, idx: number) => {
          return (
            <Box $m={[5]} key={`menu_item_${idx}`}>
              <Button
                onClick={() => {
                  window.open(link, newTab ? '_blank' : '_self');
                }}
                $variant="link"
                $size="md"
                $color="white"
                $hoverTextDecoration="underline"
              >
                {label}
              </Button>
            </Box>
          );
        })}
      </Footer>
    </div>
  );
};

export default Layout;
